import React, {useContext, useEffect, useMemo, useState} from 'react';
import AppContext from '../../appContext';
import {DataTable} from '../../components/DataTable';
import Dialog from '../../components/Dialog';
import Switch from '../../components/Switch';
import CheckBox from '../../components/CheckBox';
import CropType from '../../controllers/CropType';
import LandResponse from '../../controllers/LandResponse';
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs';
import LimeProgramLandSetup from '../../controllers/LimeProgramLandSetup';
import {useValidation} from '../../validation';
import {TableColumn} from '../../components/PagedSearchTable';
import LimeSoilDataDialog from './LimeSoilDataDialog';

interface LimeLandWithData {
    land: LandResponse;
    selected: boolean;
    labInputs: LimeProgramLabInputs;
}

interface SetupLimeLandFieldsProps {
    lands: LandResponse[];
    cropType: CropType;
    next: (cropType: CropType, lands: LimeProgramLandSetup[]) => void;
}

const SetupLimeLandFields: React.FC<SetupLimeLandFieldsProps> = (props) => {
    const app = useContext(AppContext);

    const [cropType, setCropType] = useState<CropType>(props.cropType);
    const [landsWithData, setLandsWithData] = useState<LimeLandWithData[]>([]);
    const [showSoilData, setShowSoilData] = useState(false);
    const [selectedLandIndex, setSelectedLandIndex] = useState<number | null>(null);

    const isPerennial = cropType === CropType.Perennial;

    useEffect(() => {
        setCropType(props.cropType);
    }, [props.cropType]);

    useEffect(() => {
        setLandsWithData(props.lands.map(land => ({
            land,
            selected: false,
            labInputs: {rows: []}
        })));
    }, [props.lands]);

    const selected = useMemo(() => landsWithData.filter(l => l.selected), [landsWithData]);
    const allSelected = selected.length === landsWithData.length && landsWithData.length > 0;

    const validation = useValidation({
        atLeastOneLand: selected.length > 0
    });

    function setAll(checked: boolean) {
        setLandsWithData(prev => prev.map(l => ({
            ...l,
            selected: checked
        })));
    }

    function toggleSelection(index: number) {
        setLandsWithData(prev => prev.map((l, i) =>
            i === index ? {...l, selected: !l.selected} : l
        ));
    }

    function showSoilDataPopup(index: number) {
        setSelectedLandIndex(index);
        setShowSoilData(true);
    }

    function saveSoilData(data: LimeProgramLabInputs) {
        if (selectedLandIndex === null) return;

        setLandsWithData(prev => prev.map((l, i) =>
            i === selectedLandIndex ? {...l, labInputs: data, selected: true} : l
        ));
        setShowSoilData(false);
        setSelectedLandIndex(null);
    }

    function next() {
        if (!validation.validate()) return;

        const result: LimeProgramLandSetup[] = selected.map((item, index) => ({
            landId: item.land.id,
            order: index,
            labInputs: item.labInputs
        }));

        props.next(cropType, result);
    }

    const columns: TableColumn<LimeLandWithData>[] = useMemo(() => [
        {
            header: <div className="text-center">
                <div>{app.word('select')}</div>
                <CheckBox value={allSelected} onChange={checked => setAll(checked)}/>
            </div>,
            row: (r, index) => <CheckBox value={r.selected} onChange={() => toggleSelection(index)}/>
        },
        {
            header: app.wordUnit('land_name', 'ranch_name'),
            row: (r) => r.land.name
        },
        {
            header: app.word('soil_data'),
            row: (r, index) => <div
                className="bg-primary btn-sm cursor-pointer"
                onClick={() => showSoilDataPopup(index)}
            >
                {r.labInputs.rows.length}
            </div>
        }
    ], [allSelected, landsWithData]);

    const selectedLandData = selectedLandIndex !== null ? landsWithData[selectedLandIndex] : null;

    return <div className="p-2">
        <Dialog
            title={app.word('soil_data')}
            show={showSoilData}
            setShow={setShowSoilData}
            body={selectedLandData ? (
                <LimeSoilDataDialog
                    data={selectedLandData.labInputs}
                    onSave={saveSoilData}
                    onDiscard={() => {
                        setShowSoilData(false);
                        setSelectedLandIndex(null);
                    }}
                />
            ) : null}
        />

        <div className="text-xl underline tracking-wider">3. {app.word('fields_and_blocks')}</div>

        <div className="flex my-1 items-center">
            <label className='flex items-center'>
                <span className='text-xl'>{app.word('annual')}</span>
                <div className='mx-3 my-2'>
                    <Switch
                        checked={isPerennial}
                        offColor='bg-primary'
                        setChecked={v => setCropType(v ? CropType.Perennial : CropType.Annual)}
                    />
                </div>
                <span className='text-xl'>{app.word('perennial')}</span>
            </label>
        </div>

        <div>
            <div className="inline-block my-2">
                {app.word('you_have_selected') + ' ' + selected.length + ' ' + app.wordUnit('lands', 'ranches')}
            </div>

            {landsWithData.length === 0 ? (
                <div className="text-gray-500 my-4">{app.word('no_fields_available')}</div>
            ) : (
                <DataTable
                    headerClass="bg-gray-200 font-normal text-center"
                    dataClass="text-center p-1"
                    keyExtractor={r => r.land.id}
                    definitions={columns}
                    data={landsWithData}
                />
            )}
        </div>

        <div className="text-error-500">
            {!validation.rules.atLeastOneLand ? app.wordUnit('at_least_one_land', 'at_least_one_ranch') : ''}
        </div>

        {validation.dirtyPassed && (
            <div className="text-right my-2">
                <div className="btn bg-primary" onClick={next}>{app.word('next')}</div>
            </div>
        )}
    </div>;
};

export default SetupLimeLandFields;
